import React, { FC } from 'react';
import styled from 'styled-components';
import { Select, Typography } from 'antd';
import { useSelector } from 'react-redux';
import type { Specialists } from '../../model';
import SpecialistCard from '../SpecialistCard';
import { DEVICES } from '../../styles/breakpoint';
import { swapSpecialistOrder } from '../../utils/swapSpecialistOrder';
import { StylePageSubtitle, StylePageTitle } from '../../styles/sharedStyleComponets';
import { getSpecialistStepConfig } from '../../config/privateBooking';
import { useGetCentresQuery } from '../../services/centres';
import { useAppDispatch } from '../../hooks/redux-hooks';
import { selectBookingData, updateMetadata } from '../../redux/slices/booking';
import FeeChangeBand from '../FeeChangeBand';
import { useFeeChangeNoticeOnPsychologistList } from '../../hooks/useFeeChangeNoticeOnPsychologistList';

type Props = {
  specialists: Specialists;
  openCalendar: boolean;
};

const ListContainer = styled.div`
  display: grid;
  grid-template-columns: repeat(1, minmax(0, 1fr));
  gap: 40px;

  ${DEVICES.desktop} {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
`;

const TitleContainer = styled.div<{ $compactBottomMargin: boolean }>`
  margin: ${({ $compactBottomMargin }) => ($compactBottomMargin ? '0 auto' : '0 auto 64px')};

  ${DEVICES.desktop} {
    width: 80%;
    max-width: 992px;
  }
`;

const CentreSelectContainer = styled.div`
  margin: 0 auto 24px;
  width: 100%;

  ${DEVICES.desktop} {
    width: 60%;
    max-width: 640px;
  }
`;

const RequiredAsterisk = styled.span`
  color: #ff4d4f;
  margin-right: 4px;
`;

const DEFAULT_SPECIALIST_LIST_SUBTITLE = 'Choose a psychologist who best suits your schedule. Our team offers telehealth video sessions anywhere in Australia.';

const SpecialistList: FC<Props> = ({ specialists, openCalendar }) => {
  const dispatch = useAppDispatch();
  const { metadata } = useSelector(selectBookingData);
  const specialistConfig = getSpecialistStepConfig();
  const isFeeNoticeVisible = useFeeChangeNoticeOnPsychologistList();
  const requiresCentreSelection = specialistConfig?.requireCentreSelection ?? false;
  const { data: centres, isLoading: centresLoading } = useGetCentresQuery(undefined, {
    skip: !requiresCentreSelection,
  });

  const applyOrderingRules = (list: Specialists) => {
    if (!list || !specialistConfig?.orderingRules?.length) {
      return list;
    }

    return specialistConfig.orderingRules.reduce<Specialists>((accumulator, rule) => {
      if (rule.swap) {
        return swapSpecialistOrder(accumulator, rule.swap.sourceId, rule.swap.targetId);
      }
      return accumulator;
    }, list);
  };

  const orderedSpecialists = applyOrderingRules(specialists);
  const handleCentreSelect = (value?: string) => {
    const selectedCentre = centres?.find((centre) => centre.locationId === value);
    dispatch(updateMetadata({
      centreId: selectedCentre?.locationId,
      centreName: selectedCentre?.name,
    }));
  };

  return (
    <div>
      <TitleContainer $compactBottomMargin={isFeeNoticeVisible}>
        <StylePageTitle level={2}>
          Find a session
        </StylePageTitle>
        <StylePageSubtitle>
          {specialistConfig?.listSubtitle ?? DEFAULT_SPECIALIST_LIST_SUBTITLE}
        </StylePageSubtitle>
      </TitleContainer>

      {isFeeNoticeVisible && <FeeChangeBand />}

      {requiresCentreSelection && (
        <CentreSelectContainer>
          <Typography.Text style={{ display: 'block', textAlign: 'left', marginBottom: 8, fontWeight: 500 }}>
            <RequiredAsterisk>*</RequiredAsterisk>
            Select a Centre
          </Typography.Text>
          <Select<string>
            allowClear
            placeholder="Select a centre"
            loading={centresLoading}
            value={metadata?.centreId}
            onChange={(value) => handleCentreSelect(value as (string | undefined))}
            style={{ width: '100%' }}
            options={centres?.map((centre) => ({
              label: centre.name,
              value: centre.locationId,
            }))}
          />
        </CentreSelectContainer>
      )}

      <ListContainer>
        {orderedSpecialists?.map((specialist) => (
          <SpecialistCard specialist={specialist} openCalendar={openCalendar} />
        ))}
      </ListContainer>

      {/* {showNotification && <PriceChangeNotification />} */}
    </div>
  );
};

export default SpecialistList;
